import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
  TextInput,
} from 'platform/components';
import {Grid, Hide, Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNotNil} from 'ramda';

import {useGetSeriesListQuery} from '@dms/api/core';
import {
  useCreateRentalSettingsMutation,
  useGetRentalSettingsQuery,
  useListRentalSettingsQuery,
  useUpdateRentalSettingsMutation,
} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, useBranches} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface PutDefinitionFormProps extends TestIdProps {
  settingsId?: string;
}

type FormValues = {
  branchId: string | null;
  orderSeriesId: string;
};

export function PutDefinitionForm(props: PutDefinitionFormProps) {
  const {branchOptions, isLoading: isLoadingBranches} = useBranches();
  const {data: existingSettings, isLoading: isLoadingExistingSettings} = useListRentalSettingsQuery(
    {}
  );
  const {
    data: series,
    isLoading: isLoadingSeries,
    isError: isErrorSeries,
  } = useGetSeriesListQuery({type: ['car-rental/rental-order']});

  const {
    data: currentSettings,
    isFetching: isFetchingSettings,
    isError: isErrorSettings,
  } = useGetRentalSettingsQuery(
    {rentalSettingsId: props.settingsId ?? ''},
    {skip: !props.settingsId}
  );

  const [createSettings, {isLoading: isLoadingCreate}] = useCreateRentalSettingsMutation();
  const [updateSettings, {isLoading: isLoadingUpdate}] = useUpdateRentalSettingsMutation();

  const isCreating = !props.settingsId;
  const isSystem = currentSettings?.branchId === null;
  const isLoading = isLoadingBranches || isLoadingSeries || isLoadingExistingSettings;
  const isError = isErrorSeries || isErrorSettings;

  const usedBranchIds = new Set(
    existingSettings
      ?.filter((settings) => settings.branchId !== null)
      .map((settings) => settings.branchId) ?? []
  );

  const availableBranchOptions = branchOptions.filter(
    (option) =>
      !usedBranchIds.has(option.value) ||
      (!isCreating && option.value === currentSettings?.branchId)
  );

  const seriesOptions =
    series?.map((item) => ({
      value: item.id,
      label: item.name,
    })) ?? [];

  const defaultValues: FormValues = {
    branchId: currentSettings?.branchId ?? null,
    orderSeriesId: currentSettings?.orderSeriesId ?? '',
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    if (!isCreating && props.settingsId) {
      await updateSettings({
        rentalSettingsId: props.settingsId,
        updateRentalSettingsRequestBody: values,
      })
        .unwrap()
        .then(() => {
          closeCurrentDialog();
        })
        .catch(handleApiError);

      return;
    }

    await createSettings({
      createRentalSettingsRequestBody: values,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const schema = z.object({
    branchId: isSystem ? z.string().nullable() : z.string(),
    orderSeriesId: z.string().min(1),
  });

  return (
    <DataStatus isLoading={isLoading || isFetchingSettings} isError={isError} minHeight={25}>
      <Form<FormValues>
        onSubmit={handleSubmit}
        experimentalZodSchema={schema}
        defaultValues={defaultValues}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={2}>
              <Hide when={isNotNil(currentSettings) && currentSettings?.branchId === null}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  label={i18n.t('entity.branch.labels.branch')}
                  data-testid={suffixTestId('branchId', props)}
                  options={availableBranchOptions}
                  menuInPortal
                  isRequired
                  isNotClearable
                />
              </Hide>

              <Show when={isNotNil(currentSettings) && currentSettings?.branchId === null}>
                <TextInput
                  isDisabled
                  label={i18n.t('entity.rental.labels.tenantDefinition')}
                  value={i18n.t('entity.accounting.labels.tenantSeries')}
                />
              </Show>

              <FormField
                control={control}
                type="choice"
                name="orderSeriesId"
                label={i18n.t('entity.rental.labels.series')}
                data-testid={suffixTestId('orderSeriesId', props)}
                options={seriesOptions}
                menuInPortal
                isRequired
                isNotClearable
                labelActions={[
                  {
                    href: settingsRoutes.seriesDefinition,
                    title: i18n.t('entity.rental.labels.goToSeriesSettings'),
                    rightIcon: 'action/arrow_right_alt',
                  },
                ]}
              />
            </Grid>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                data-testid={suffixTestId('cancel', props)}
                onClick={closeCurrentDialog}
              />
              <FormButton
                control={control}
                isLoading={isLoadingCreate || isLoadingUpdate}
                type="submit"
                data-testid={suffixTestId('submit', props)}
                title={i18n.t(isCreating ? 'general.actions.create' : 'general.actions.save')}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
